import { ArrowLeft, Menu, X, Building2, LayoutDashboard, Briefcase, FileText, Settings, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useState } from 'react';
import CompanySelectorSidebar from '@/components/wizard/CompanySelectorSidebar';

const WizardLayout = ({ children, currentStep, title, company, showSidebar = true, showCompanySelector = false, onSelectCompany }) => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const steps = [
    { id: 1, name: 'Solutions' },
    { id: 2, name: 'Configuration' },
    { id: 3, name: 'Upload' },
    { id: 4, name: 'Processing' }
  ];

  const sidebarItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Building2, label: 'My Companies', path: '/companies' },
    { icon: Briefcase, label: 'Jobs', path: '/jobs' },
    { icon: FileText, label: 'Invoices', path: '/invoices' },
    { icon: Gift, label: 'Referrals', path: '/referrals' },
    { icon: Settings, label: 'Settings', path: '/settings' }
  ];

  return (
    <div className="min-h-screen flex bg-background">
      {/* Company Selector Sidebar */}
      {showCompanySelector && (
        <CompanySelectorSidebar 
          selectedCompanyId={company?.id} 
          onSelectCompany={onSelectCompany}
        />
      )}

      {/* Collapsible Navigation Sidebar */}
      {showSidebar && (
        <div className={`${sidebarCollapsed ? 'w-16' : 'w-64'} border-r border-border bg-card transition-all duration-300 flex flex-col`}>
          <div className="p-4 border-b border-border flex items-center justify-between">
            {!sidebarCollapsed && <h1 className="font-bold">iXBRL</h1>}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="ml-auto"
            >
              {sidebarCollapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex-1 p-3 space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors text-sm relative"
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top bar with back button */}
        <div className="border-b border-border bg-card px-6 h-16 flex items-center">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/companies')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Exit Wizard
              </Button>
              {company && (
                <>
                  <div className="w-px h-6 bg-border"></div>
                  <div>
                    <p className="text-sm font-medium">{company.name}</p>
                    <p className="text-xs text-muted-foreground">{company.registrationNumber}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Horizontal stepper */}
        <div className="wizard-stepper">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`wizard-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
                <div className="wizard-step-number">
                  {currentStep > step.id ? <CheckCircle className="w-4 h-4" /> : step.id}
                </div>
                <span className="text-sm font-medium">{step.name}</span>
              </div>
              {index < steps.length - 1 && <div className="wizard-connector"></div>}
            </div>
          ))}
        </div>

        {/* Content area */}
        <div className="flex-1 p-8 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default WizardLayout;